import React from "react";
import { useDispatch } from "react-redux";
// hooks
import useDialog from "hooks/useDialog";
// Actions
import { PropertyOwnershipsActions } from "store/propertyOwnerships/propertyOwnershipsSlice";
// Models
import IPropertyOwnership from "models/PropertyOwnership";
// components
import ReservationRequestForm from "components/ReservationRequest.form";
// mui
import { makeStyles } from "@mui/styles";
import {
  Box, Button, Card, CardContent,
  Chip, Tooltip, Typography
} from "@mui/material";
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
// utilities
import { formatStartEndDate } from "utilities/DateTimeFormatter";

type Props = {
  propertyOwnership: IPropertyOwnership,
}

const PropertyOwnershipCard:React.FC<Props> = ({ propertyOwnership }) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const imgUrl = propertyOwnership.property.images ? propertyOwnership.property.images[0].url : propertyOwnership.images ? propertyOwnership.images[0].url : '/img/noPropertyFound.png';

  const goToDetails = () => {
    window.open(`/timeshares/${propertyOwnership.id}`, '_blank');
  }

  const openRequest = (event: any) => {
    event.stopPropagation();
    dispatch(PropertyOwnershipsActions.setPropertyOwnershipDetails(propertyOwnership));
    openDialog();
  }

  const { Dialog, openDialog, closeDialog } = useDialog();

  return (
    <React.Fragment>
      <Dialog maxWidth="md">
        <ReservationRequestForm onClose={closeDialog} />
      </Dialog>

      <Card className={classes.card} onClick={goToDetails}>
        <div className={classes.gradient} />
        <Chip
          color={propertyOwnership.start ? 'default' : 'success'} size="small"
          icon={<EventAvailableOutlinedIcon />}
          label={propertyOwnership.start ? formatStartEndDate(propertyOwnership.start, propertyOwnership.end) : 'Flexible dates'}
          sx={{ position: 'absolute', top: '16px', left: '16px', backgroundColor: propertyOwnership.start ? '#fff' : "auto" }}
        />
        <img src={imgUrl} style={{ width: '100%', height: '260px', objectFit: 'cover' }} alt={propertyOwnership.property.name} />
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Tooltip title={propertyOwnership?.property.name} arrow>
              <Typography className={classes.title}>
                {propertyOwnership?.property.name}
              </Typography>
            </Tooltip>
            <Tooltip title={`${propertyOwnership?.property.region}, ${propertyOwnership?.property.country}`} arrow>
              <Typography className={classes.subtitle}>
                {`${propertyOwnership?.property.region}, ${propertyOwnership?.property.country}`}
              </Typography>
            </Tooltip>
          </Box>
          <Button
            sx={{ p: 1 }}
            variant="contained"
            onClick={openRequest}
            startIcon={<RequestQuoteOutlinedIcon />}
          >
            Request a quote
          </Button>
        </CardContent>
      </Card>
    </React.Fragment>
  )
}

export default PropertyOwnershipCard;

const useStyles = makeStyles({
  card: {
    width: '100%',
    position: 'relative',
    cursor: 'pointer',
    flexGrow: 1,
  },
  title: {
    fontWeight: 500,
    fontSize: '20px',
    lineHeight: '160%',
    letterSpacing: '-0.35px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: '100%',
  },
  subtitle: {
    fontSize: '14px',
    lineHeight: '143%',
    letterSpacing: '-0.43px',
    color: 'rgba(0, 0, 0, 0.6)',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: '100%',
  },
  gradient: {
    position: 'absolute',
    width: '100%',
    height: '120px',
    left: 0,
    top: 0,
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 100%)',
  },
});
